import React from 'react';
import { Link } from 'react-router-dom';
import { Check, X, Droplets, AlertTriangle, ShieldCheck, Sparkles } from 'lucide-react';
import PageShell from '@/components/PageShell';
import SectionDivider from '@/components/SectionDivider';

const comparisonRows: { feature: string; vapour: string; sand: string; vapourGood: boolean }[] = [
  { feature: 'Surface finish', vapour: 'Smooth, satin, factory-fresh', sand: 'Rough, etched, pitted', vapourGood: true },
  { feature: 'Dust during blasting', vapour: 'Virtually none — water traps it', sand: 'Heavy airborne silica dust', vapourGood: true },
  { feature: 'Heat & warping risk', vapour: 'Low — water cools the part', sand: 'High — can warp alloy & thin steel', vapourGood: true },
  { feature: 'Safe on aluminium & alloy', vapour: 'Yes — preserves tolerances', sand: 'No — strips & deforms soft metals', vapourGood: true },
  { feature: 'Removes oil, carbon & grease', vapour: 'Yes — cleans and degreases in one pass', sand: 'No — drives contaminants into surface', vapourGood: true },
  { feature: 'Threads, bearings & sealing faces', vapour: 'Protected — gentle on critical surfaces', sand: 'Damaged — media embeds in threads', vapourGood: true },
  { feature: 'Best for', vapour: 'Engines, carbs, alloy wheels, marine, restoration', sand: 'Heavy steel, structural, paint prep', vapourGood: true },
  
];

const faqs = [
  {
    q: 'Is vapour blasting the same as sandblasting?',
    a: 'No. Sandblasting fires dry abrasive at high pressure and leaves a rough, etched surface — fine for structural steel but destructive to alloy. Vapour blasting (also called wet blasting or aqua blasting) suspends a fine glass-bead media in water, giving a smooth satin finish without removing parent metal. For engine, carb, alloy wheel and marine work in Christchurch, vapour blasting is the right choice.'
  },
  {
    q: 'Will sandblasting damage my engine parts?',
    a: 'Usually yes. Dry sandblasting is aggressive enough to open up tolerances on bearing surfaces, embed media in threads and oil galleries, and warp thin alloy castings. Vapour blasting removes carbon, oil and oxidation while leaving the parent metal untouched — exactly what restorers and rebuilders need.'
  },
  {
    q: "Why is vapour blasting better for alloy wheels?",
    a: 'Alloy is soft. Sand etches it, dulls the casting detail, and creates a porous surface that powder coating struggles to bond to cleanly. Vapour blasting brings alloy wheels back to a smooth factory satin in one pass and is the preferred prep for refinishing or clear-coating.'
  },
  {
    q: 'Do you offer sandblasting in Christchurch as well?',
    a: 'We specialise in vapour blasting because it produces a better result for almost every job people bring us. If you genuinely need heavy dry blasting for structural steel or paint stripping a chassis, get in touch — we will tell you honestly whether vapour blasting suits the job or point you to someone who does dry blasting.'
  },
  {
    q: 'Is vapour blasting more expensive than sandblasting?',
    a: 'Per hour it is comparable. Per finished result it is usually cheaper — there is no re-cleaning, no warped parts to replace, no media to dig out of threads, and the finish is ready for assembly or paint straight off the gun.'
  },
];

const VapourVsSandblasting = () => {
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: 'Vapour Blasting vs Sandblasting in Christchurch',
      about: 'Comparison of vapour blasting and sandblasting for engine, alloy and restoration work in Christchurch, New Zealand.',
      author: { '@type': 'Organization', name: 'Precision Vapour Blasting' },
      publisher: { '@type': 'Organization', name: 'Precision Vapour Blasting' },
    },
  ];

  return (
    <PageShell
      title="Sandblasting Christchurch? Why Vapour Blasting Is Better"
      description="Searching for sandblasting in Christchurch? For engine parts, alloy wheels and restorations, vapour blasting cleans without pitting or grit damage. See the difference."
      canonicalPath="/vapour-blasting-vs-sandblasting"
      crumbs={[{ name: 'Vapour Blasting vs Sandblasting', path: '/vapour-blasting-vs-sandblasting' }]}
      jsonLd={jsonLd}
    >
      {/* Hero */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 bg-gradient-to-b from-background via-background to-card overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none bg-[radial-gradient(circle_at_30%_20%,hsl(var(--primary))_0%,transparent_50%)]" />
        <div className="container-wide mx-auto px-4 md:px-8 relative">
          <p className="text-primary font-oswald uppercase tracking-[0.3em] text-sm mb-4">
            Christchurch · Honest comparison
          </p>
          <h1 className="font-oswald text-4xl md:text-6xl lg:text-7xl uppercase leading-tight text-foreground mb-6 max-w-4xl">
            Vapour Blasting <span className="text-primary">vs</span> Sandblasting
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl leading-relaxed">
            Most people in Christchurch searching for <em>“sandblasting”</em> actually want
            vapour blasting — they just have not heard the term. If the part is an engine
            casing, carburettor, alloy wheel, marine fitting or anything you care about
            keeping straight, sand is the wrong tool. Here is the side-by-side.
          </p>
          <p className="text-muted-foreground max-w-3xl mt-4">
            Searching the US spelling? See our{' '}
            <Link to="/vapor-blasting" className="text-primary hover:underline">
              vapor blasting page
            </Link>
            .
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link
              to="/contact"
              className="bg-primary text-primary-foreground font-oswald uppercase tracking-wider px-8 py-3 hover:bg-primary/90 transition-colors"
            >
              Get a quote
            </Link>
            <Link
              to="/services"
              className="border border-border text-foreground font-oswald uppercase tracking-wider px-8 py-3 hover:border-primary hover:text-primary transition-colors"
            >
              See our services
            </Link>
          </div>
        </div>
      </section>

      <SectionDivider />

      {/* Quick-take cards */}
      <section className="py-16 md:py-20 bg-background">
        <div className="container-wide mx-auto px-4 md:px-8 grid md:grid-cols-2 gap-6">
          <div className="bg-card border border-border p-8 relative overflow-hidden">
            <Droplets className="h-10 w-10 text-primary mb-4" />
            <h2 className="font-oswald text-3xl uppercase text-foreground mb-3">Vapour Blasting</h2>
            <p className="text-muted-foreground mb-6">
              Fine glass media suspended in water. Cleans, degreases and finishes in one
              pass with no heat, no dust and no metal loss.
            </p>
            <ul className="space-y-2 text-foreground/90">
              {['Smooth satin factory finish','Safe on alloy, brass and thin steel','Removes oil, carbon and oxidation','No warping or media embedding'].map((t) => (
                <li key={t} className="flex items-start gap-2"><Check className="h-5 w-5 text-primary mt-0.5 shrink-0" /><span>{t}</span></li>
              ))}
            </ul>
          </div>
          <div className="bg-card border border-border p-8 relative overflow-hidden opacity-90">
            <AlertTriangle className="h-10 w-10 text-muted-foreground mb-4" />
            <h2 className="font-oswald text-3xl uppercase text-foreground mb-3">Sandblasting</h2>
            <p className="text-muted-foreground mb-6">
              Dry abrasive at high pressure. Excellent for structural steel and paint
              stripping — wrong tool for engine parts, alloy or anything precision.
            </p>
            <ul className="space-y-2 text-foreground/90">
              {['Rough etched surface','Strips and warps alloy','Drives dust into threads and galleries','Often needs re-cleaning before assembly'].map((t) => (
                <li key={t} className="flex items-start gap-2"><X className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" /><span>{t}</span></li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <SectionDivider />

      {/* Comparison table */}
      <section className="py-16 md:py-20 bg-card">
        <div className="container-wide mx-auto px-4 md:px-8">
          <h2 className="font-oswald text-3xl md:text-5xl uppercase text-foreground mb-3">
            Side-by-side comparison
          </h2>
          <p className="text-muted-foreground mb-10 max-w-2xl">
            How vapour blasting and sandblasting stack up for the jobs Christchurch
            customers bring us most.
          </p>

          <div className="overflow-x-auto border border-border">
            <table className="w-full text-left">
              <thead className="bg-background">
                <tr>
                  <th className="font-oswald uppercase tracking-wider text-sm p-4 text-muted-foreground">Feature</th>
                  <th className="font-oswald uppercase tracking-wider text-sm p-4 text-primary">Vapour Blasting</th>
                  <th className="font-oswald uppercase tracking-wider text-sm p-4 text-muted-foreground">Sandblasting</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, i) => (
                  <tr key={row.feature} className={i % 2 === 0 ? 'bg-card' : 'bg-background/50'}>
                    <td className="p-4 font-medium text-foreground align-top">{row.feature}</td>
                    <td className="p-4 text-foreground/90 align-top">
                      <span className="flex items-start gap-2"><Check className="h-4 w-4 text-primary mt-1 shrink-0" />{row.vapour}</span>
                    </td>
                    <td className="p-4 text-muted-foreground align-top">
                      <span className="flex items-start gap-2"><X className="h-4 w-4 text-muted-foreground mt-1 shrink-0" />{row.sand}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <SectionDivider />

      {/* Which is right for me */}
      <section className="py-16 md:py-20 bg-background">
        <div className="container-wide mx-auto px-4 md:px-8 grid md:grid-cols-3 gap-6">
          {[
            { icon: Sparkles, title: 'Restoring an engine or carb?', body: 'Use Vapour blasting. Sand will ruin tolerances and embed media in oil galleries.' },
            { icon: ShieldCheck, title: 'Refinishing alloy wheels?', body: 'Use Vapour blasting. Sand etches alloy and dulls casting detail — vapour leaves a clean satin ready for coating.' },
            { icon: AlertTriangle, title: 'Stripping a steel chassis or trailer?', body: 'Sandblasting is fine here. Give us a call and we will point you to a reputable sandblaster.' },
          ].map(({ icon: Icon, title, body }) => (
            <div key={title} className="bg-card border border-border p-6">
              <Icon className="h-8 w-8 text-primary mb-3" />
              <h3 className="font-oswald uppercase text-xl text-foreground mb-2">{title}</h3>
              <p className="text-muted-foreground">{body}</p>
            </div>
          ))}
        </div>
      </section>

      <SectionDivider />

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-card">
        <div className="container-wide mx-auto px-4 md:px-8 max-w-4xl">
          <h2 className="font-oswald text-3xl md:text-5xl uppercase text-foreground mb-10">
            Common questions
          </h2>
          <div className="space-y-6">
            {faqs.map((f) => (
              <div key={f.q} className="border-l-2 border-primary pl-6">
                <h3 className="font-oswald uppercase text-lg text-foreground mb-2">{f.q}</h3>
                <p className="text-muted-foreground leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SectionDivider />

      {/* CTA */}
      <section className="py-20 bg-background">
        <div className="container-wide mx-auto px-4 md:px-8 text-center max-w-3xl">
          <h2 className="font-oswald text-3xl md:text-5xl uppercase text-foreground mb-4">
            Not sure which you need?
          </h2>
          <p className="text-muted-foreground text-lg mb-8">
            Send a photo of the part and what you want to achieve. We will tell you
            straight whether vapour blasting is right for the job — and quote it the
            same day.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-primary text-primary-foreground font-oswald uppercase tracking-wider px-10 py-4 hover:bg-primary/90 transition-colors"
          >
            Get a free quote
          </Link>
        </div>
      </section>
    </PageShell>
  );
};

export default VapourVsSandblasting;
